import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Grid, Typography, Button, CircularProgress, } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, } from "recharts";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, } from "recharts";
import { getTransactionReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "portal",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "QBot", value: "qbot" },
            { label: "PayCentral", value: "paycentral" },
            { label: "KYC", value: "kyc" },
            { label: "Tripola", value: "tripola" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Success", value: "1" },
            { label: "Failed", value: "2" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const TransactionReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [summary, setSummary] = useState({
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        successRate: 0,
    });
    const [transactions, setTransactions] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const portalChartData = [
        { name: "QBot", value: transactions.filter((item) => item.portal === "QBot").length },
        { name: "PayCentral", value: transactions.filter((item) => item.portal === "PayCentral").length },
        { name: "KYC", value: transactions.filter((item) => item.portal === "KYC").length },
        { name: "Tripola", value: transactions.filter((item) => item.portal === "Tripola").length },
    ];

    const statusChartData = [
        { name: "Success", value: summary.successfulTransactions },
        { name: "Failed", value: summary.failedTransactions },
    ];

    const buildApiFilters = (fv) => ({
        portal: fv.portal === "all" ? "" : fv.portal,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getTransactionReportAction(buildApiFilters(fv));
            const reportData = response?.data?.data;
            setSummary(reportData?.summary || {});
            setTransactions(reportData?.transactions || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    const hasAppliedFilters =
        filterValues.portal !== "all" ||
        filterValues.status !== "all";

    return (
        <>
            <Grid container spacing={3}>
                {/* Filter Section */}
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">Transaction Report</h2>
                                </div>

                                {/* Buttons */}
                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* Summary Cards */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#EEF4FF", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Transactions</Typography>
                            <Typography variant="h4">{summary.totalTransactions}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#e9f8f0", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Successful</Typography>
                            <Typography variant="h4">{summary.successfulTransactions}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#f8f1f3", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Failed</Typography>
                            <Typography variant="h4">{summary.failedTransactions}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#FFF7E6", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Success Rate</Typography>
                            <Typography variant="h4">{summary.successRate}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Charts */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    {/* Status Chart */}
                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Transaction Status
                                </Typography>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={8}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Box height={350}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={statusChartData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    outerRadius={110}
                                                    label
                                                >
                                                    <Cell fill="#16a34a" />
                                                    <Cell fill="#ef4444" />
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Portal Chart */}
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Transactions By Portal
                                </Typography>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={8}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Box height={350}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={portalChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                                    {portalChartData.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={
                                                                entry.name === "QBot"
                                                                    ? "#22419e"
                                                                    : entry.name === "PayCentral"
                                                                        ? "#16a34a"
                                                                        : entry.name === "KYC"
                                                                            ? "#8b5cf6"
                                                                            : "#f97316"
                                                            }
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TransactionReport;